import { categoryLevel, parseFloodCategory, type FloodCategory } from '../config/gauges';
import { fetchJson, HttpError } from '../lib/fetchJson';
import { parseISODate } from '../lib/format';
import type { GaugeSeverityResult, NWPSFloodCategories, NWPSGaugeResponse, StageFlowPoint, StageFlowResponse } from '../types/gauges';

const BASE = 'https://api.water.noaa.gov/nwps/v1/gauges';

export class GaugeNotFoundError extends Error {
  constructor(readonly lid: string) {
    super(`Gauge '${lid}' not found (HTTP 404).`);
    this.name = 'GaugeNotFoundError';
  }
}

export function fetchNWPSGauge(lid: string, signal?: AbortSignal): Promise<NWPSGaugeResponse> {
  const id = lid.trim().toUpperCase();
  return fetchJson<NWPSGaugeResponse>(`${BASE}/${encodeURIComponent(id)}`, { signal, timeoutMs: 15_000 }).catch((err) => {
    if (err instanceof HttpError && err.status === 404) throw new GaugeNotFoundError(id);
    throw err;
  });
}

export function fetchStageFlow(lid: string, signal?: AbortSignal): Promise<StageFlowResponse> {
  const id = lid.trim().toUpperCase();
  return fetchJson<StageFlowResponse>(`${BASE}/${encodeURIComponent(id)}/stageflow`, { signal, timeoutMs: 15_000 });
}

/** Lookup by USGS site number (NWPSService.fetchGaugeStatus); first match or null. */
export async function fetchNWPSGaugeByUsgsId(usgsId: string, signal?: AbortSignal): Promise<NWPSGaugeResponse | null> {
  const data = await fetchJson<{ gauges?: NWPSGaugeResponse[] }>(`${BASE}?usgsId=${encodeURIComponent(usgsId)}`, {
    signal,
    timeoutMs: 15_000,
  });
  return data.gauges?.[0] ?? null;
}

/** NWPS uses -9999 for "no threshold defined". */
export function validStage(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value) && value > -9000;
}

/**
 * First forecast point where the stage moves into a new flood category
 * (NOAAGaugeService.nextStageCrossing). Rising: the first point at or above the
 * lowest threshold above the current stage. Falling: the first point below the
 * highest threshold at or below the current stage, which drops the gauge into
 * the category beneath.
 */
export function nextStageCrossing(
  currentStage: number,
  rising: boolean,
  categories: NWPSFloodCategories,
  forecast: StageFlowPoint[],
  now: Date = new Date(),
): { category: FloodCategory; time: Date } | null {
  const thresholds: { stage: number; category: FloodCategory }[] = (
    [
      [categories.action?.stage, 'action'],
      [categories.minor?.stage, 'minor'],
      [categories.moderate?.stage, 'moderate'],
      [categories.major?.stage, 'major'],
    ] as [number | null | undefined, FloodCategory][]
  )
    .filter(([stage]) => stage != null)
    .map(([stage, category]) => ({ stage: stage as number, category }));

  const upcoming = forecast
    .map((p) => ({ time: parseISODate(p.validTime), primary: p.primary }))
    .filter((p): p is { time: Date; primary: number } => p.time != null && p.time.getTime() >= now.getTime());

  if (rising) {
    const target = thresholds.find((t) => t.stage > currentStage);
    if (!target) return null;
    const point = upcoming.find((p) => p.primary >= target.stage);
    return point ? { category: target.category, time: point.time } : null;
  }
  let index = -1;
  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (thresholds[i].stage <= currentStage) {
      index = i;
      break;
    }
  }
  if (index < 0) return null;
  const point = upcoming.find((p) => p.primary < thresholds[index].stage);
  if (!point) return null;
  return { category: index > 0 ? thresholds[index - 1].category : 'no_flooding', time: point.time };
}

/** Whether the forecast crest sits above the observed stage; null when units differ or a reading is missing. */
export function forecastIsRising(observedStage: number | null, observedUnit: string | null, forecastStage: number | null, forecastUnit: string | null): boolean | null {
  if (observedStage == null || forecastStage == null) return null;
  if (observedUnit && forecastUnit && observedUnit !== forecastUnit) return null;
  return forecastStage > observedStage;
}

/**
 * Observed and forecast severity for one gauge, plus the next threshold
 * crossing for gauges at action stage or above (NOAAGaugeService.severity).
 */
export async function fetchGaugeSeverity(lid: string, signal?: AbortSignal): Promise<GaugeSeverityResult> {
  const gauge = await fetchNWPSGauge(lid, signal);
  return severityFromGauge(gauge, async () => fetchStageFlow(lid, signal));
}

export async function severityFromGauge(
  gauge: NWPSGaugeResponse,
  loadStageFlow: () => Promise<StageFlowResponse>,
  now: Date = new Date(),
): Promise<GaugeSeverityResult> {
  const obs = gauge.status?.observed ?? null;
  const fct = gauge.status?.forecast ?? null;
  const result: GaugeSeverityResult = {
    gaugeID: gauge.lid,
    gaugeName: gauge.name,
    observed: parseFloodCategory(obs?.floodCategory),
    observedStage: obs?.primary ?? null,
    observedUnit: obs?.primaryUnit ?? null,
    observedValidTime: obs?.validTime ?? null,
    forecast: parseFloodCategory(fct?.floodCategory),
    forecastStage: fct?.primary ?? null,
    forecastUnit: fct?.primaryUnit ?? null,
    forecastValidTime: fct?.validTime ?? null,
    forecastPeakTime: parseISODate(fct?.validTime),
    forecastIsRising: forecastIsRising(obs?.primary ?? null, obs?.primaryUnit ?? null, fct?.primary ?? null, fct?.primaryUnit ?? null),
    nextStage: null,
    nextStageTime: null,
  };

  const worst = Math.max(categoryLevel(result.observed), categoryLevel(result.forecast));
  const categories = gauge.flood?.categories ?? null;
  if (worst >= categoryLevel('action') && categories && result.observedStage != null && result.forecastIsRising != null) {
    try {
      const stageFlow = await loadStageFlow();
      const forecast = stageFlow.forecast;
      if (
        forecast &&
        (forecast.primaryUnits == null || result.observedUnit == null || forecast.primaryUnits === result.observedUnit)
      ) {
        const crossing = nextStageCrossing(result.observedStage, result.forecastIsRising, categories, forecast.data ?? [], now);
        if (crossing) {
          result.nextStage = crossing.category;
          result.nextStageTime = crossing.time;
        }
      }
    } catch {
      // The hydrograph is optional; the severity result stands without it.
    }
  }
  return result;
}
